/** @jsx jsx */
import { useCallback, ChangeEvent } from 'react';

import { pluralize } from '@zapier/common-utils';
import { Colors, Grid, Text } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

const THUMB_SIZE = 18;
const TRACK_HEIGHT = 5;

const Styles = {
  rangeSlider: css`
    height: 22px;
    outline: none;
    -webkit-appearance: none;

    &:focus {
      outline: none;

      &::-webkit-slider-thumb {
        box-shadow: 0 0 0 2px ${Colors.PrimeWhite}, 0 0 0 4px ${Colors.Blue3};
      }

      &::-moz-range-thumb {
        box-shadow: 0 0 0 2px ${Colors.PrimeWhite}, 0 0 0 4px ${Colors.Blue3};
      }
    }

    &::-webkit-slider-runnable-track {
      background: ${Colors.GrayWarm3};
      border: 0;
      border-radius: 12px;
      cursor: pointer;
      height: ${TRACK_HEIGHT}px;
      -webkit-appearance: none;
    }

    &::-webkit-slider-thumb {
      background: ${Colors.Blue3};
      border: 0;
      border-radius: 10px;
      cursor: ew-resize;
      height: ${THUMB_SIZE}px;
      margin-top: ${0 - THUMB_SIZE / 2 + TRACK_HEIGHT / 2}px;
      width: ${THUMB_SIZE}px;
      transition: box-shadow 100ms ease-in-out;
      -webkit-appearance: none;
    }

    &::-moz-range-track {
      background: ${Colors.GrayWarm3};
      border: 0;
      border-radius: 10px;
      cursor: pointer;
      height: ${TRACK_HEIGHT}px;
    }

    &::-moz-range-thumb {
      background: ${Colors.Blue3};
      border: 0;
      border-radius: 10px;
      cursor: ew-resize;
      height: ${THUMB_SIZE}px;
      margin-top: ${0 - THUMB_SIZE / 2 + TRACK_HEIGHT / 2}px;
      width: ${THUMB_SIZE}px;
      transition: box-shadow 100ms ease-in-out;
    }

    &::-moz-focus-inner {
      border: 0;
    }

    &::-moz-focus-outer {
      border: 0;
    }
  `,
};

type Props = {
  /** aria-label to be applied to the input */
  ariaLabel?: string;
  /** Maximum range value */
  max: number;
  /** Minimum range value */
  min: number;
  /** Callback to be executed when the input's value is changed. */
  onChange: (value: number) => void;
  /** Show the value below the slider */
  showValue?: boolean;
  /** Step interval of slider */
  step: number;
  /** Current value of slider */
  value: number;
  /** Label to show for value (used with showValue) */
  valueLabel?: string;
};

const RangeSlider = ({
  ariaLabel = 'Range Slider',
  max = 100,
  min = 0,
  value,
  step = 1,
  valueLabel,
  onChange,
  showValue = false,
}: Props) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      onChange && onChange(Number(e.target.value)),
    [onChange]
  );

  return (
    <Grid alignItems="center" columns={showValue ? '2fr 1fr' : 1} gap={10}>
      <input
        aria-label={ariaLabel}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={value}
        css={Styles.rangeSlider}
        max={max}
        min={min}
        onChange={handleChange}
        step={step}
        type="range"
        value={value}
      />
      {showValue && (
        <Text>
          {value}
          {valueLabel ? ` ${pluralize(valueLabel, value)}` : ''}
        </Text>
      )}
    </Grid>
  );
};

export default RangeSlider;
